import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  TableContainer,
  HStack,
  Tooltip,
  InputGroup,
  InputLeftElement,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  useToast
} from '@chakra-ui/react';
import { 
  SearchIcon, 
  QuestionIcon, 
  AddIcon, 
  TriangleDownIcon,
  TriangleUpIcon,
  ViewIcon,
  EditIcon,
  EmailIcon
} from '@chakra-ui/icons';
import { format } from 'date-fns';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { htmlToText } from 'html-to-text';
import { emailTemplateService } from '../services/emailTemplateService';
import { RichTextEditor } from './RichTextEditor';
import RichTextPreview from './RichTextEditor/Preview';
import { EditorHelp } from './RichTextEditor/EditorHelp';

const EmailTemplateManager = () => {
  const supabase = useSupabaseClient();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [editedSubject, setEditedSubject] = useState('');
  const [editedTextContent, setEditedTextContent] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [hasContentChanges, setHasContentChanges] = useState(false);
  const [canConvertToPlainText, setCanConvertToPlainText] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [testEmail, setTestEmail] = useState('john.doe@example.com');
  const [testName, setTestName] = useState('John Doe');
  const [testPhone, setTestPhone] = useState('+15005550006');
  const [clubName, setClubName] = useState('EP Noon Rotary');
  const [previewData, setPreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const editorRef = useRef(null);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();
  const helpModal = useDisclosure();
  const previewModal = useDisclosure();

  useEffect(() => {
    loadTemplates();
    loadUserDefaults();
  }, []);

  const loadUserDefaults = async () => {
    setIsLoading(true);
    try {
      // Get user info and profile
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone_number')
        .eq('id', user.id)
        .single();

      if (user && profile) {
        setTestEmail(user.email);
        setTestName(`${profile.first_name} ${profile.last_name}`);
        setTestPhone(profile.phone_number || '');

        // Get club name from preferences
        const { data: preferences } = await supabase
          .from('club_preferences')
          .select('club_name')
          .single();

        if (preferences?.club_name) {
          setClubName(preferences.club_name);
        }
      }

    } catch (error) {
      console.error('Error loading user defaults:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const templates = await emailTemplateService.loadTemplates(supabase);
      setTemplates(templates);
    } catch (error) {
      toast({
        title: 'Error loading templates',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditedContent(template.html_content || template.htmlContent || '');
    setEditedSubject(template.subject || '');
    setEditedTextContent(template.text_content || template.textContent || '');
    setHasChanges(false);
    setHasContentChanges(false);
    setCanConvertToPlainText(false);
    setCurrentTab(0);
    onOpen();
  };

  const handleListPreview = (template) => {
    setPreviewData({
      type: 'html',
      content: template.html_content || template.htmlContent,
      subject: template.subject
    });
    previewModal.onOpen();
  };

  const handleSendTestFromRow = async (template) => {
    try {
      setIsSending(true);
      
      const templateToSend = {
        id: template.id,
        name: template.name,
        subject: template.subject,
        html_content: template.html_content,
        text_content: template.text_content,
        test_email: testEmail
      };

      await emailTemplateService.sendTestEmail({
        template: templateToSend,
        testName,
        clubName,
        testPhone,
        supabase
      });

      toast({
        title: 'Test email sent',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error sending test email',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!selectedTemplate) {
      toast({
        title: 'No template selected',
        description: 'Please select a template before sending a test email.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSending(true);
      
      const templateToSend = {
        subject: editedSubject || selectedTemplate.subject,
        html_content: editedContent || selectedTemplate.html_content,
        text_content: editedTextContent || selectedTemplate.text_content,
        test_email: testEmail
      };

      console.log('Sending template:', templateToSend);

      await emailTemplateService.sendTestEmail({
        template: templateToSend,
        testName,
        clubName,
        testPhone
      });

      toast({
        title: 'Test email sent',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      toast({
        title: 'Error sending test email',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleContentChange = (content, isHtml = true) => {
    if (isHtml) {
      setEditedContent(content);
      setCanConvertToPlainText(true);
    } else {
      setEditedTextContent(content);
    }
    setHasChanges(true);
  };

  const handleSubjectChange = (e) => {
    setEditedSubject(e.target.value);
    setHasChanges(true);
  };

  const handleConvertToPlainText = () => {
    setIsConverting(true);
    try {
      const plainText = htmlToText(editedContent, {
        wordwrap: 130,
        selectors: [
          { selector: 'a', options: { hideLinkHrefIfSameAsText: true } },
          { selector: 'img', format: 'skip' }
        ]
      });
      setEditedTextContent(plainText);
      setIsConverting(false);
      setCanConvertToPlainText(false);
      
      toast({
        title: 'Plain Text Updated',
        description: 'HTML content has been converted to plain text.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error converting to plain text:', error);
      toast({
        title: 'Error converting to plain text',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsConverting(false);
    }
  };

  const handlePreview = () => {
    setPreviewData({
      type: currentTab === 0 ? 'html' : 'text',
      content: currentTab === 0 ? editedContent : editedTextContent,
      subject: editedSubject
    });
    previewModal.onOpen();
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updatedTemplate = await emailTemplateService.saveTemplate(supabase, {
        ...selectedTemplate,
        subject: editedSubject,
        html_content: editedContent,
        text_content: editedTextContent,
      });

      setTemplates(templates.map(t => 
        t.id === updatedTemplate.id ? updatedTemplate : t
      ));

      toast({
        title: 'Template saved',
        description: 'Email template has been updated successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setHasChanges(false);
    } catch (error) {
      toast({
        title: 'Error saving template',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleVariableInsert = (variable) => {
    if (variable === 'sendDate') {
      // Show date format menu
      const now = new Date();
      const options = [
        {
          label: `Full - ${format(now, 'EEEE, MMMM dd, yyyy')}`,
          value: 'sendDate:full'
        },
        {
          label: `Long - ${format(now, 'MMMM dd, yyyy')}`,
          value: 'sendDate:long'
        },
        {
          label: `Medium - ${format(now, 'MMM dd, yyyy')}`,
          value: 'sendDate:medium'
        },
        {
          label: `Short - ${format(now, 'M/d/yyyy')}`,
          value: 'sendDate:short'
        }
      ];

      const menuItems = options.map(option => ({
        onClick: () => editorRef.current?.insertAtCursor(`{{${option.value}}}`),
        label: option.label
      }));

      // Show menu using Chakra UI's useDisclosure
      setDateFormatMenu({
        isOpen: true,
        items: menuItems
      });
    } else {
      const variableText = `{{${variable}}}`;
      editorRef.current?.insertAtCursor(variableText);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    template.subject.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    const aValue = a[sortField]?.toLowerCase() || '';
    const bValue = b[sortField]?.toLowerCase() || '';
    return sortDirection === 'asc' 
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });

  return (
    <Box p={4}>
      <HStack justify="space-between" mb={6}>
        <HStack spacing={100}>
          <Heading size="lg">Manage Email Templates</Heading>
          <IconButton
            icon={<AddIcon />}
            colorScheme="blue"
            onClick={() => handleEdit({})}
            size="sm"
            aria-label="Add Template"
          />
        </HStack>
        <IconButton
          icon={<QuestionIcon />}
          variant="ghost"
          aria-label="Help"
          size="sm"
          onClick={helpModal.onOpen}
        />
      </HStack>

      <Stack spacing={4}>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search templates by name or subject..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>

        <TableContainer borderWidth="1px" borderRadius="lg" shadow="sm">
          <Table variant="simple" size="xs">
            <Thead>
              <Tr>
                <Th cursor="pointer" onClick={() => handleSort('name')}>
                  <HStack spacing={1}>
                    <Text>TEMPLATE NAME</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'name') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'name' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'name' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th cursor="pointer" onClick={() => handleSort('subject')}>
                  <HStack spacing={1}>
                    <Text>SUBJECT</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'subject') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'subject' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'subject' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th cursor="pointer" onClick={() => handleSort('updated_at')}>
                  <HStack spacing={1}>
                    <Text>LAST MODIFIED</Text>
                    <Stack spacing={-1} direction="column">
                      {(!sortField || sortField !== 'updated_at') && (
                        <>
                          <Icon as={TriangleUpIcon} boxSize={2} color="gray.400" />
                          <Icon as={TriangleDownIcon} boxSize={2} color="gray.400" />
                        </>
                      )}
                      {sortField === 'updated_at' && sortDirection === 'asc' && (
                        <Icon as={TriangleUpIcon} boxSize={2} color="black" />
                      )}
                      {sortField === 'updated_at' && sortDirection === 'desc' && (
                        <Icon as={TriangleDownIcon} boxSize={2} color="black" />
                      )}
                    </Stack>
                  </HStack>
                </Th>
                <Th isNumeric>ACTIONS</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedTemplates.map((template) => (
                <Tr key={template.id}>
                  <Td>{template.name}</Td>
                  <Td>{template.subject}</Td>
                  <Td>{format(new Date(template.updated_at || template.created_at), 'MM/dd/yyyy')}</Td>
                  <Td isNumeric>
                    <HStack spacing={1} justify="flex-end">
                      <Tooltip label="View template">
                        <IconButton
                          icon={<ViewIcon />}
                          onClick={() => handleListPreview(template)}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="View Template"
                        />
                      </Tooltip>
                      <Tooltip label="Edit template">
                        <IconButton
                          icon={<EditIcon />}
                          onClick={() => handleEdit(template)}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="Edit Template"
                        />
                      </Tooltip>
                      <Tooltip label="Send test email">
                        <IconButton
                          icon={<EmailIcon />}
                          onClick={() => handleSendTestFromRow(template)}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="Send Test Email"
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        <Box mt={6}>
          <Heading size="sm" mb={2}>Test Settings</Heading>
          <Text fontSize="sm" color="gray.600" mb={4}>
            These settings are used to preview and test your email templates with sample data.
          </Text>
          <HStack spacing={4}>
            <FormControl>
              <FormLabel>First and Last Name</FormLabel>
              <Input
                value={testName}
                onChange={(e) => setTestName(e.target.value)}
                placeholder="Enter test name"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone Number</FormLabel>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="Enter test phone"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter test email"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Club Name</FormLabel>
              <Input
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                placeholder="Enter club name"
              />
            </FormControl>
          </HStack>
        </Box>
      </Stack>

      <Modal 
        isOpen={isOpen} 
        onClose={onClose} 
        size="full" 
        scrollBehavior="inside"
        portalProps={{ containerRef: null }}  
      >
        <ModalOverlay />
        <ModalContent maxW="90vw" h="95vh" my="2.5vh" display="flex" flexDirection="column">
          <ModalHeader p={4} flexShrink={0} display="flex" alignItems="center" borderBottomWidth="1px">
            <Box flex="1">Edit Template: {selectedTemplate?.name}</Box>
            <HStack spacing={2}>
              {currentTab === 0 && (
                <Button
                  variant="outline"
                  onClick={handleConvertToPlainText}
                  isDisabled={!canConvertToPlainText}
                  isLoading={isConverting}
                >
                  Convert to Plain Text
                </Button>
              )}
              <Button
                variant="outline"
                onClick={handlePreview}
              >
                Preview
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isDisabled={!hasChanges}
                isLoading={isSaving}
              >
                Save Changes
              </Button>
              <IconButton
                icon={<QuestionIcon />}
                variant="ghost"
                aria-label="Help"
                onClick={helpModal.onOpen}
              />
            </HStack>
          </ModalHeader>

          <ModalBody p={4} flex={1} display="flex" flexDirection="column" overflow="hidden">
            <FormControl mb={4}>
              <FormLabel>Subject</FormLabel>
              <Input
                value={editedSubject}
                onChange={handleSubjectChange}
                placeholder="Enter email subject"
              />
            </FormControl>

            <Box mb={4}>
              <Heading size="sm" mb={2}>Message Editor with Dynamic Variables</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                Type your message directly or customize a template from above.
                Add dynamic content by clicking any variable below - they'll update automatically when sent.
              </Text>
              <Box p={2} bg="gray.50" borderRadius="md">
                <HStack spacing={2} wrap="wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVariableInsert('firstName')}
                    _hover={{ bg: "blue.50" }}
                  >
                    First Name
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVariableInsert('lastName')}
                    _hover={{ bg: "blue.50" }}
                  >
                    Last Name
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVariableInsert('fullName')}
                    _hover={{ bg: "blue.50" }}
                  >
                    Full Name
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVariableInsert('email')}
                    _hover={{ bg: "blue.50" }}
                  >
                    Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVariableInsert('clubName')}
                    _hover={{ bg: "blue.50" }}
                  >
                    Club Name
                  </Button>
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="outline"
                      _hover={{ bg: "blue.50" }}
                      rightIcon={<TriangleDownIcon />}
                    >
                      Phone
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => handleVariableInsert('phone:unformattedLong')}>
                        Unformatted long - +18887779999
                      </MenuItem>
                      <MenuItem onClick={() => handleVariableInsert('phone:unformattedShort')}>
                        Unformatted short - 8887779999
                      </MenuItem>
                      <MenuItem onClick={() => handleVariableInsert('phone:formattedLong')}>
                        Formatted long - +1 (888) 777-9999
                      </MenuItem>
                      <MenuItem onClick={() => handleVariableInsert('phone:formattedShort')}>
                        Formatted short - (888) 777-9999
                      </MenuItem>
                    </MenuList>
                  </Menu>
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="outline"
                      _hover={{ bg: "blue.50" }}
                      rightIcon={<TriangleDownIcon />}
                    >
                      Send Date
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => handleVariableInsert('sendDate:full')}>
                        Full - {format(new Date(), 'EEEE, MMMM dd, yyyy')}
                      </MenuItem>
                      <MenuItem onClick={() => handleVariableInsert('sendDate:long')}>
                        Long - {format(new Date(), 'MMMM dd, yyyy')}
                      </MenuItem>
                      <MenuItem onClick={() => handleVariableInsert('sendDate:medium')}>
                        Medium - {format(new Date(), 'MMM dd, yyyy')}
                      </MenuItem>
                      <MenuItem onClick={() => handleVariableInsert('sendDate:short')}>
                        Short - {format(new Date(), 'M/d/yyyy')}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </Box>
            </Box>

            <Box flex={1} minH={0} overflow="hidden">
              <RichTextEditor
                ref={editorRef}
                htmlContent={editedContent}
                textContent={editedTextContent}
                onHtmlChange={(content) => handleContentChange(content, true)}
                onTextChange={(content) => handleContentChange(content, false)}
                onTabChange={setCurrentTab}
              />
            </Box>
          </ModalBody>

          <ModalFooter borderTopWidth="1px" bg="gray.50" p={4} flexShrink={0}>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={previewModal.isOpen}
        onClose={previewModal.onClose}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxW="90vw" h="95vh" my="2.5vh" display="flex" flexDirection="column">
          <ModalHeader p={4} flexShrink={0} display="flex" alignItems="center" borderBottomWidth="1px">
            <Box flex="1">Preview Template</Box>
          </ModalHeader>

          <ModalBody p={4} flex={1} display="flex" flexDirection="column" overflow="hidden">
            <RichTextPreview
              content={previewData?.content || ''}
              type={previewData?.type || 'html'}
              subject={previewData?.subject}
              isOpen={previewModal.isOpen}
              onClose={previewModal.onClose}
            />
          </ModalBody>

          <ModalFooter borderTopWidth="1px" bg="gray.50" p={4} flexShrink={0}>
            <Button
              colorScheme="blue"
              onClick={previewModal.onClose}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <EditorHelp isOpen={helpModal.isOpen} onClose={helpModal.onClose} />
    </Box>
  );
};

export default EmailTemplateManager;
